import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  Subject,
  Subscription,
} from 'rxjs';
import { IEnvironment } from '../../../models/environment.model';

import { updateUrlWithApiGatewayBaseUrl } from '../../../../utils/utils/data-fetch-widget-utils';
import { FormStateService } from '../../../services/formly/form-state.service';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';
import {
  alphaNanoIdUtil,
  convertObjectToQueryString,
  getValueFromNestedObject,
  PageEvent,
  SortEvent,
} from '../../../../../public-api';
import {
  IIntegrationFetchSyncRequest,
  IIntegrationFetchSyncRequestResponse,
} from '../../../models/integration-fetch-sync.model';

const INTEGRATION_URL = '/integration/v1/fetch/sync';

@Component({
  standalone: false,
  selector: 'irembogov-custom-data-table',
  templateUrl: './custom-data-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomDataTableComponent
  extends FieldType<FieldTypeConfig>
  implements OnDestroy, OnInit
{
  private readonly environment: IEnvironment;
  private readonly subscriptions = new Subscription();
  private readonly searchSubject = new Subject<string>();

  useBaseUrl = true;
  usePrefetch = false;
  applicationBusinessId: string | undefined;
  loading = false;
  pagination = {
    size: 10,
    page: 1,
    collectionSize: 0,
  };

  params: Record<string, unknown> = {};

  minSelection: number | null = null;

  constructor(
    @Inject('environment') private readonly env: IEnvironment,
    private readonly http: HttpClient,
    private readonly formStateService: FormStateService,
    private readonly cd: ChangeDetectorRef
  ) {
    super();
    this.environment = env;
  }

  ngOnInit(): void {
    const { pageSize } = this.field.props;
    this.pagination.size = pageSize ?? 10;

    if (this.field.props.disabled) {
      this.formControl.disable();
    }

    this.subscriptions.add(
      this.field?.options?.fieldChanges
        ?.pipe(
          filter(e => {
            return (
              e.type === 'expressionChanges' &&
              e.field === this.field &&
              e['property'] === 'props.disabled'
            );
          })
        )
        .subscribe(res => {
          if (res.value) {
            this.formControl.disable();
          } else {
            this.formControl.enable();
          }
        })
    );

    this.subscriptions.add(
      this.searchSubject
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(value => {
          const {
            zeroBasedPageIndex,
            searchKeywordKey,
            searchable,
            currentPageKey,
          } = this.field.props;
          if (searchable && searchKeywordKey) {
            const currentPage = zeroBasedPageIndex ? 0 : 1;
            this.params[searchKeywordKey] = value;
            this.params[currentPageKey] = currentPage;
            this.fetchDataAsync();
          }
        })
    );

    if (this.field.props['asyncData'] || false) {
      this.fetchDataAsync();
    }

    // Sync props.items to formControl when not selectable (for preview support)
    if (!this.field.props['selectable']) {
      const items = this.field.props['items'];
      if (Array.isArray(items) && items.length > 0) {
        this.formControl.setValue(items);
      }
    }

    this.cd.detectChanges();
  }

  fetchDataAsync() {
    const { dataset, pageSizeKey, currentPageKey, zeroBasedPageIndex } =
      this.field.props;

    if (!dataset) return;

    if (!dataset['url'] && !dataset['endpointCode']) {
      throw new Error('Invalid dataset property');
    }

    const httpMethod = dataset?.['httpMethod'];
    let url = dataset['url'];
    const headers = dataset['headers'];
    const dataField = dataset['dataField'];
    const endpointCode = dataset['endpointCode'];
    const useBaseUrl = dataset['useBaseUrl'];

    if (useBaseUrl) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.environment);
    }

    if (pageSizeKey) {
      this.params[pageSizeKey] = this.pagination.size;
    }
    if (currentPageKey) {
      const currentPage = zeroBasedPageIndex ? 0 : 1;
      this.params[currentPageKey] = this.params[currentPageKey] ?? currentPage;
    }

    const params = { ...dataset['params'], ...this.params };

    if (httpMethod === 'POST') {
      this.getDataByPostMethod(url, dataField, params, headers, endpointCode);
    } else {
      this.getDataByGetMethod(url, dataField, params, headers);
    }
  }

  getDataByPostMethod(
    url: string,
    dataField: string,
    params: Record<string, unknown> = {},
    headers: Record<string, string> = {},
    endpointCode?: string
  ) {
    this.loading = true;
    if (endpointCode) {
      const url = updateUrlWithApiGatewayBaseUrl(
        INTEGRATION_URL,
        this.environment
      );
      this.getDataByIntegrationFetchSync(endpointCode, url, dataField, params);
    } else {
      this.subscriptions.add(
        this.http
          .post<Record<string, unknown>>(url, params, { headers })
          .pipe(
            finalize(() => {
              this.loading = false;
              this.cd.detectChanges();
            })
          )
          .subscribe({
            next: res => {
              this.prepareTableData(res, dataField);
            },
            error: () => {
              this.deleteFetchedOptionsDataKeyInFormState();
              this.cd.detectChanges();
            },
          })
      );
    }
  }

  getDataByGetMethod(
    url: string,
    dataField: string,
    params: Record<string, unknown> = {},
    headers: Record<string, string> = {}
  ) {
    const queryParams = convertObjectToQueryString(params).replace(/%2C/g, ',');
    this.loading = true;
    this.subscriptions.add(
      this.http
        .get<Record<string, unknown>>(`${url}?${queryParams}`, { headers })
        .pipe(
          finalize(() => {
            this.loading = false;
            this.cd.detectChanges();
          })
        )
        .subscribe({
          next: res => {
            this.prepareTableData(res, dataField);
          },
          error: () => {
            this.deleteFetchedOptionsDataKeyInFormState();
            this.cd.detectChanges();
          },
        })
    );
  }

  getDataByIntegrationFetchSync(
    endpointCode: string,
    url: string,
    dataField: string,
    additionalParams: Record<string, unknown>
  ): void {
    const req: IIntegrationFetchSyncRequest = {
      callerId: alphaNanoIdUtil(50),
      endpointCode: endpointCode,
      requester: alphaNanoIdUtil(50),
      payload: additionalParams,
    };

    this.subscriptions.add(
      this.http
        .post<IIntegrationFetchSyncRequestResponse>(`${url}`, req)
        .pipe(
          finalize(() => {
            this.loading = false;
            this.cd.detectChanges();
          })
        )
        .subscribe({
          next: res => {
            this.prepareTableData(res, dataField, true);
          },
          error: () => {
            this.deleteFetchedOptionsDataKeyInFormState();
            this.cd.detectChanges();
          },
        })
    );
  }

  saveFetchedOptionsDataKeyInFormState(data: any): void {
    this.formStateService.saveFetchedDataKeyInFormState(
      this.field,
      data,
      EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA,
      false
    );
  }

  deleteFetchedOptionsDataKeyInFormState(): void {
    this.formStateService.deleteFetchedDataKeyInFormState(
      this.field,
      EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA
    );
  }

  handleSelection(selectedRows: Record<string, unknown>[]) {
    this.formStateService.saveFetchedDataKeyInFormState(
      this.field,
      selectedRows,
      EIremboFormlyFormStateKeys.FETCHED_DATA,
      false
    );
  }

  handleSort(event: SortEvent) {
    const { sortKey, sortDirectionKey, zeroBasedPageIndex, currentPageKey } =
      this.field.props;

    if (!sortKey) return;

    this.params[sortKey] = event.column;
    if (sortDirectionKey) {
      this.params[sortDirectionKey] = event.direction;
    } else if (event.direction) {
      this.params[sortKey] = `${this.params[sortKey]},${event.direction}`;
    }

    if (currentPageKey) {
      this.pagination.page = zeroBasedPageIndex ? 0 : 1;
      this.params[currentPageKey] = this.pagination.page;
    }

    this.fetchDataAsync();
  }

  handlePagnition(event: PageEvent) {
    const { zeroBasedPageIndex, currentPageKey } = this.field.props;
    this.pagination.page = zeroBasedPageIndex ? event.page - 1 : event.page;
    this.params[currentPageKey] = this.pagination.page;
    this.fetchDataAsync();
  }

  handleSearch(keyword: string) {
    this.searchSubject.next(keyword);
  }

  private deepParseJson(
    input: unknown
  ): Record<string, unknown> | Record<string, unknown>[] | unknown {
    if (typeof input !== 'string') {
      return input as Record<string, unknown>;
    }
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) {
        return parsed.map(item => this.deepParseJson(item)) as Record<
          string,
          unknown
        >[];
      }

      if (parsed && typeof parsed === 'object') {
        const result: Record<string, unknown> = {};
        for (const key in parsed) {
          if (Object.prototype.hasOwnProperty.call(parsed, key)) {
            result[key] = this.deepParseJson(parsed[key]);
          }
        }
        return result;
      }
      return parsed;
    } catch (error) {
      console.error('Json parse error:', error);
      return input;
    }
  }

  private prepareTableData(res: any, dataField: string, isIntegration = false) {
    try {
      if (res.status) {
        const { collectionSizeKey } = this.field.props;

        let response = res;
        if (isIntegration) {
          response =
            typeof res.data.response === 'string'
              ? this.deepParseJson(res.data.response)
              : res.data.response;
        }

        if (Array.isArray(response)) {
          this.field.props['items'] = response;
          this.pagination.collectionSize = this.field.props['items'].length;
        } else if (typeof response === 'object' && response !== null) {
          const items = this.getValueByKey(response, dataField);
          this.field.props['items'] = items;
          const totalElements = this.getValueByKey(response, collectionSizeKey);
          if (totalElements && typeof totalElements === 'number') {
            this.pagination.collectionSize = totalElements;
          } else {
            this.pagination.collectionSize = items.length;
          }
        }
        this.saveFetchedOptionsDataKeyInFormState(this.field.props['items']);
      }
      this.cd.detectChanges();
    } catch (error) {
      console.error('Error processing response:', error);
      this.field.props['items'] = [];
      this.cd.detectChanges();
    }
  }

  private getValueByKey(obj: any, key: string) {
    if (!key) return undefined;

    if (key.includes('.')) {
      return getValueFromNestedObject(obj, key);
    }

    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }

    for (const prop in obj) {
      if (
        Object.prototype.hasOwnProperty.call(obj, prop) &&
        typeof obj[prop] === 'object' &&
        obj[prop] !== null
      ) {
        const result: any = this.getValueByKey(obj[prop], key);
        if (result !== undefined) {
          return result;
        }
      }
    }

    return undefined;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
